import React from 'react';
import styled from 'styled-components';
import DefaultLayout from '../../layouts/DefaultLayout';
import TemplateColumn, { TemplateColumnWithSidebarContainer } from '../../layouts/TemplateColumn';
import { ReferencesBlock, PageHeader, PageSection, PageSubSection, RelatedContent, CrossLink, IndicationPageIntro, AnchorLinks, PrimaryClaim, SecondaryCallout, KMCurve, ComparisonTable, EndpointDisplay, PatientProfile, TextBlock } from '../../components';
import { StaticImage } from 'gatsby-plugin-image';
import MSI_CRC from '../../components/StudyDesign/__study-design/msi-crc'
import { jobCodes } from '../../util/data/jobCodes';
import { NotationBlock, Heading } from '../../util/typography';

// SEO INFORMATION
const pageMeta = {
    title: `KEYNOTE-177 - Clinical Trial Results | HCP`,
    keywords: `keynote 177, msi-h colorectal cancer clinical trial results`,
    description: `Health care professionals may find clinical trial results for KEYNOTE-177 in patients with advanced MSI-H/dMMR colorectal cancer (CRC).`,
    schemaJsonLD: [
        `{"@context":"http://schema.org/","url":"https://www.keytrudahcp.com/efficacy/msi-h-dmmr-colorectal-cancer/","@type":"MedicalTrial","name":"KEYNOTE⁠-⁠177","description":"Clinical Findings from KEYNOTE⁠-⁠177","trialDesign":"The efficacy of KEYTRUDA was investigated in KEYNOTE⁠-⁠177, a multicenter, randomized, open-label, active-controlled trial that enrolled 307 patients with previously untreated unresectable or metastatic MSI⁠-⁠H or dMMR CRC."}`,
        `{"@context":"http://schema.org/","url":"https://www.keytrudahcp.com/efficacy/msi-h-dmmr-colorectal-cancer/","@type":"MedicalAudience","audienceType":"Health Care Professionals","geographicArea":"This site is intended for health care professionals of the United States, its territories, and Puerto Rico."}`
    ]
}

//from /src/util/data/indications.js
const indicationId = 23;
const jobCode = jobCodes[23].jobCode;

const LinkContent = styled.div`
    div>div:last-of-type{
    a:last-child {
    margin-bottom:0;
    }
    }
`;

const patientProfileEvelyn = {
    image: () => (
        <StaticImage layout="fixed" src="../../assets/patient-evelyn.png" placeholder="TRACED_SVG" alt="" width={100} height={100} />
    ),
    name: "Evelyn",
    diagnosis: "Advanced MSI-H CRC with several unresectable metastatic lesions in the liver",
    gridContainer: true,
    primaryCharacteristics: [
        {
            key: 'Colonoscopy',
            value: 'A 5cm, non-obstructive mass in ascending colon',
            emphasize: false,
        },
        {
            key: 'Biopsy',
            value: 'Moderately differentiated adenocarcinoma',
            emphasize: false,
        },
        {
            key: 'CT Scan',
            value: 'Right colonic mass and multiple liver lesions of various sizes consistent with metastases',
            emphasize: false,
        },
        {
            key: 'Gender',
            value: 'Female',
        },
        {
            key: 'Age',
            value: '73',
            emphasize: false,
        },
        {
            key: 'ECOG PS',
            value: '1',
            emphasize: false,
        },
        
    ],
    secondaryCharacteristics: [
        // {
        //     key: 'Gender',
        //     value: 'Female',
        // },
        // {
        //     key: 'Family History',
        //     value: 'No family history',
        // }
    ],
};

const patientProfileBryan = {
    image: () => (
        <StaticImage layout="fixed" src="../../assets/patient-bryan.png" placeholder="TRACED_SVG" alt="" width={100} height={100} />
    ),
    name: "Bryan",
    diagnosis: "Advanced MSI-H CRC with a single unresectable metastatic lesion in the liver",
    gridContainer: true,
    primaryCharacteristics: [
        {
            key: 'Colonoscopy',
            value: 'Mass on the left side of the colon',
            emphasize: false,
        },
        {
            key: 'Biopsy',
            value: 'Moderately differentiated adenocarcinoma',
            emphasize: false,
        },
        {
            key: 'CT Scan',
            value: 'Mass on the left side of the colon, enlarged lymph nodes adjacent to left colon mass, and a single large metastasis in the liver',
            emphasize: false,
        },
        {
            key: 'Gender',
            value: 'Male',
        },
        {
            key: 'Age',
            value: '65',
            emphasize: false,
        },
        {
            key: 'ECOG PS',
            value: '0',
            emphasize: false,
        },
    ],
    secondaryCharacteristics: [
        // {
        //     key: 'Gender',
        //     value: 'Male',
        // },
        // {
        //     key: 'Family History',
        //     value: 'Colorectal cancer on father’s side',
        // }
    ],
    definitions: 'BMI = body mass index; CT = computed tomography.'
};

const pageReferences = [
    {
        label: "1.",
        text: "Andre T, Shiu K, Kim TW, et al. Pembrolizumab versus chemotherapy in microsatellite instability-high/mismatch repair-deficient metastatic colorectal cancer: 5-year follow-up of the randomized phase 3 KEYNOTE-177 study. <i>Ann Oncol</i>. 2024;1-8. doi:10.1016/j.annonc.2024.11.012"
    }
]

const ChartTitle = styled(Heading.H4)``;

const TemplateESsecondary = styled.div`
    position: relative;
    z-index: 6;
`;

const relatedContent = [
    {
        section: 'Resources',
        link: {
            url: '/resources/mechanism-of-action/',
            label: 'Mechanism of Action'
        }
    },
    {
        section: 'Resources',
        link: {
            url: 'https://www.merckaccessprogram-keytruda.com/',
            label: 'Access & Reimbursement Support'
        }
    }
];

const crossLinkContent = {
    sections: [
        {
            heading: "Additional Clinical Data",
            items: [
                { text: 'MSI&#8288;-&#8288;H/dMMR Cancers', url: '/efficacy/msi-h-dmmr/' },
            ]
        },
        {
            heading: "Related Information",
            items: [
                { text: 'Selected Adverse Reactions', url: '/safety/adverse-reactions/msi-h-dmmr-colorectal-cancer/' },
                { text: 'Dosing', url: '/dosing/options/#crc' },
                { text: 'MSI/MMR Testing', url: '/biomarker-testing/msi-mmr/' },
            ]
        },
    ],
    interestLinks: {
        heading: "You Might Be Interested In",
        items: [
            {
                eyebrow: 'Resources',
                links: [
                    {
                        text: 'Mechanism of Action',
                        url: '/resources/mechanism-of-action/',
                    },
                    // {
                    //     text: 'Hypothetical Patient Profile',
                    //     url: '/static/pdf/US-OVC-00878_ADT_HCP_Grace_Patient_Profile_PDF_MSI-H_CRC_L08_Interactive_Secure.pdf',
                    //     target: '_blank'
                    // },
                    {
                        text: 'Patient Cases',
                        url: '/static/pdf/US-OVC-01005_Patient_Profile.pdf',
                        target: '_blank'
                    }                   
                ]
            },
        ],
    }
};

const anchorLinkModelData = [
    { label: 'Clinical Findings from KEYNOTE&#8288;-&#8288;177' }, 
    { label: 'Study Design for KEYNOTE&#8288;-&#8288;177' },
    { label: 'Appropriate Patients' }
]

const key177SecondaryCalloutFootnotes = [
    {
        label: "a.",
        text: "mFOLFOX6 (oxaliplatin, leucovorin, and [fluorouracil] FU) or mFOLFOX6 in combination with either bevacizumab or cetuximab or FOLFIRI (irinotecan, leucovorin, and FU) or FOLFIRI in combination with either bevacizumab or cetuximab."
    },
    {
        label: "b.",
        text: "Based on Cox regression model."
    },
    {
        label: "c.",
        text: "Two-sided <i>P</i> value based on log-rank test (compared to a significance level of 0.0234)."
    },
];

const key177KMcurveFootnotes = [
    {
        label: "d.",
        text: "As assessed by BICR according to RECIST v1.1, modified to follow a maximum of 10 target lesions and a maximum of 5 target lesions per organ."
    },
    {
        label: "e.",
        text: "mFOLFOX6 (oxaliplatin, leucovorin, and [fluorouracil] FU) or mFOLFOX6 in combination with either bevacizumab or cetuximab or FOLFIRI (irinotecan, leucovorin, and FU) or FOLFIRI in combination with either bevacizumab or cetuximab."
    },
    {
        label: "f.",
        text: "Based on Cox regression model."
    },
    {
        label: "g.",
        text: "Two-sided <i>P</i> value based on log-rank test (compared to a significance level of 0.0234)."
    },
];

const Year5KMCurveFootnotes = [
    {
        label: "m.",
        text: "As assessed by BICR according to RECIST v1.1."
    },
];

const PFSComparisonFootnotes = [
    {
        label: 'h.',
        text: 'Based on Cox regression model.'
    },
    {
        label: 'i.',
        text: 'Two-sided <i>P</i> value based on log-rank test (compared to a significance level of 0.0234).'
    },
]

const OSrateFootnotes = [
    {
        label: 'j.',
        text: 'Final OS analysis.'
    },
    {
        label: 'k.',
        text: 'Based on Cox regression model.'
    },
    {
        label: 'l.',
        text: 'Two-sided <i>P</i> value based on log-rank test (compared to a significance level of 0.0492).'
    },
]

const OREndpointFootnotes = [
    {
        label: 'n.',
        text: 'Based on confirmed response by BICR review.'
    },
]

const DORtableFootnotes = [
    {
        label: 'o.',
        text: 'Based on confirmed response by BICR review.'
    },
    {
        label: 'p.',
        text: 'Based on n=67 patients with a response in the KEYTRUDA arm and n=51 patients with a response in the chemotherapy arm.'
    },
    {
        label: 'q.',
        text: '+Denotes ongoing response.'
    },
    // {
    //     label: 'r.',
    //     text: '+Indicates no PD by the time of last assessment.'
    // },
]

const Page = ({ location }) => {

    return (
        <DefaultLayout indicationId={indicationId} jobCode={jobCode} pageMeta={pageMeta} location={location}>
            <div data-template-name="template-e">

                {/* Begin Intro Column */}
                <TemplateColumn id="template-e-intro" maxWidth={824}>
                    <PageHeader title="Clinical Trial Results" />
                    <PageSection bgColor="white">
                        <IndicationPageIntro indicationId={indicationId} />
                        <AnchorLinks items={anchorLinkModelData} />
                    </PageSection>
                    <PageSection bgColor="gradient">
                        <PrimaryClaim theme="light">Single-IO Treatment Approved for First-Line Use in Advanced MSI-H/dMMR CRC</PrimaryClaim>
                    </PageSection>
                </TemplateColumn>
                {/* End Intro Column */}

                <TemplateColumnWithSidebarContainer>
                    {/* Begin Main Column */}
                    <TemplateColumn id="template-e-main">
                        <PageSection bgColor="cloud" title={anchorLinkModelData[0].label}>
                            <SecondaryCallout 
                                title="40% reduction in the risk of disease progression or death with KEYTRUDA vs chemotherapy<sup>a</sup>" 
                                text="HR<sup>b</sup>=0.60; 95% CI, 0.45–0.80; <i>P</i><sup>c</sup>=0.0004" 
                                alt="40% Reduction in the Risk of Disease Progression or Death With KEYTRUDA® (pembrolizumab) vs Chemotherapy"
                                footnotes={key177SecondaryCalloutFootnotes}
                            />
                            <KMCurve
                                title="Kaplan-Meier Estimates of PFS<sup>d</sup> With KEYTRUDA vs Chemotherapy<sup>e</sup>"
                                hr="HR<sup>f</sup>=0.60; 95% CI, 0.45–0.80; <i>P</i><sup>g</sup>=0.0004"
                                footnotes={key177KMcurveFootnotes}
                                definitions="BICR = blinded independent central review; CI = confidence interval; HR = hazard ratio; IO = immunotherapy; PD&#8288;-&#8288;1 = programmed death receptor-1; PFS = progression-free survival; RECIST v1.1 = Response Evaluation Criteria In Solid Tumors v1.1."
                                image={() => <StaticImage src="../../assets/crc-177-km-v-2.png"
                                    placeholder="blurred"
                                    alt="Kaplan-Meier Estimates of PFS in Patients With Advanced MSI-H/dMMR Colorectal Cancer (CRC) With KEYTRUDA® (pembrolizumab) vs Chemotherapy in KEYNOTE-177"
                                />} />

                            <ComparisonTable 
                                title="Progression-Free Survival (PFS): Double the median PFS with KEYTRUDA" 
                                footnotes={PFSComparisonFootnotes}
                                data={
                                    [
                                        [
                                            {
                                                legendTitle: 'KEYTRUDA (n=153)',
                                            },
                                            {
                                                legendTitle: 'Chemotherapy (n=154)',
                                            },
                                        ],
                                        [
                                            {
                                                hrCiP: 'HR<sup>h</sup>=0.60; 95% CI, 0.45–0.80; <i>P</i><sup>i</sup>=0.0004',
                                                colspan: 2,
                                                centerText: true,
                                            },
                                        ],
                                        [
                                            {
                                                label: 'Events observed',
                                                dataPoint: '54%',
                                                dataSubPoint: '(n=82/153)'
                                            },
                                            {
                                                label: 'Events observed',
                                                dataPoint: '73%',
                                                dataSubPoint: '(n=113/154)'
                                            },
                                        ],
                                        [
                                            {
                                                label: 'Median PFS',
                                                dataPoint: '16.5 months',
                                                dataSubPoint: '(95% CI, 5.4–32.4)'
                                            },
                                            {
                                                label: 'Median PFS',
                                                dataPoint: '8.2 months',
                                                dataSubPoint: '(95% CI, 6.1–10.2)'
                                            },
                                        ]
                                
                                    ]
                                }
                            />

                            {/* OS Data */}
                            <ComparisonTable 
                                title="Overall Survival (OS)<sup>j</sup>" 
                                limitation="There was no statistically significant difference between KEYTRUDA and chemotherapy in the final OS analysis. Sixty percent of the patients who had been randomized to receive chemotherapy had crossed over to receive subsequent anti–PD&#8288;-&#8288;1/PD&#8288;-&#8288;L1 therapies including KEYTRUDA. Median follow-up time at the final analysis was 38.1 months (range: 0.2 to 58.7 months)."
                                footnotes={OSrateFootnotes} 
                                definitions="PD-L1 = program death ligand 1."
                                data={
                                    [
                                        [
                                            {
                                                legendTitle: 'KEYTRUDA (n=153)',
                                            },
                                            {
                                                legendTitle: 'Chemotherapy (n=154)',
                                            },

                                        ],
                                        [
                                            {
                                                colspan: 2,
                                                centerText: true,
                                                hrCiP: 'HR<sup>k</sup>=0.74; 95% CI, 0.53–1.03; <i>P</i><sup>l</sup>=0.0718'
                                            }
                                        ],
                                        [
                                            {
                                                label: 'Median OS',
                                                dataPoint: 'Not reached (95% CI 49.2, Not reached)',
                                            },
                                            {
                                                label: 'Median OS',
                                                dataPoint: '36.7 months (95% CI 27.6, Not reached)',
                                            },
                                        ],
                                        [
                                            {
                                                label: 'Events Observed',
                                                dataPoint: '41%',
                                                dataSubPoint: '(n=62/153)'
                                            },
                                            {
                                                label: 'Events Observed',
                                                dataPoint: '51%',
                                                dataSubPoint: '(n=78/154)'
                                            },
                                        ],
                                    ]
                                } 
                            />
                                <TextBlock >
                                    <Heading.H3 as='H4'>PFS<sup>m</sup>: 5-Year Follow-Up<sup>1</sup></Heading.H3>
                                    <p><b>LIMITATIONS:</b> The 5-year follow-up of KN-⁠177 was a post hoc, exploratory analysis. No formal statistical testing was planned for this analysis and, therefore, no statistical conclusions can be drawn.</p>
                                </TextBlock>
                            <KMCurve
                                title="Kaplan-Meier Estimates of PFS With KEYTRUDA vs Chemotherapy"
                                limitation=""
                                footnotes={Year5KMCurveFootnotes}
                                hr="HR=0.60; 95% CI, 0.45–0.79"
                                // additionalInformation={[
                                //     { text: `
                                //     <ul>
                                //         <li>Median PFS (95% CI)</li>
                                //         <li>KEYTRUDA: 16.5 mo (5.4–38.1)</li>
                                //         <li>Chemotherapy: 8.2 mo (6.2–10.3)</li>
                                //     </ul>`},
                                // ]}
                                image={() => <StaticImage src="../../assets/KN-177_PFS_KM_Shiu_ESMO_2023_ADT_r11_FIN_US-OVC-00983.png"
                                    placeholder="blurred"
                                    alt="Kaplan-Meier Estimates of PFS With KEYTRUDA® (pembrolizumab) vs Chemotherapy in KEYNOTE-177"
                                />} 
                            />


                            <ComparisonTable 
                            title={`PFS: 5-Year Follow-Up<sup>1</sup>`}
                            data={
                                    [
                                        [
                                            {
                                                legendTitle: 'KEYTRUDA (n=153)',
                                            },
                                            {
                                                legendTitle: 'Chemotherapy (n=154)',
                                            },

                                        ],
                                        // [
                                        //     {
                                        //         colspan: 2,
                                        //         centerText: true,
                                        //         hrCiP: 'HR=0.60; 95% CI, 0.45-0.79'
                                        //     }
                                        // ],
                                        [
                                            {
                                                label: 'Events observed',
                                                dataPoint: '61.4%',
                                                dataSubPoint: '(n=94/153)'
                                            },
                                            {
                                                label: 'Events observed',
                                                dataPoint: '79.2%',
                                                dataSubPoint: '(n=122/154)'
                                            },
                                        ],
                                        [
                                            {
                                                label: 'Median PFS',
                                                dataPoint: '16.5 months',
                                                dataSubPoint: '(95% CI, 5.4-38.1)'
                                            },
                                            {
                                                label: 'Median PFS',
                                                dataPoint: '8.2 months',
                                                dataSubPoint: '(95% CI, 6.2-10.3)'
                                            },
                                        ]
                                    ]
                                } 
                            />

                            <TextBlock>
                                <ul>
                                    <li>Median time from randomization to data cutoff was 73.3 months (6.1 years; range: 64.9–89.2 months)</li>
                                </ul>
                            </TextBlock>

                                <ChartTitle as='H3' dangerouslySetInnerHTML={{ __html: "Objective Response Rate (ORR)<sup>n</sup>" }}/>
                                <EndpointDisplay 
                                    footnotes={OREndpointFootnotes}
                                    cards={
                                        [
                                            {
                                                dataType: 'ORR',
                                                label: 'KEYTRUDA',
                                                rate: '44%',
                                                n: '67/153',
                                                ci: '95% CI, 35.8–52.0',
                                                completeResponseRate: '11%',
                                                partialResponseRate: '33%',
                                            },
                                            {
                                                dataType: 'ORR',
                                                label: 'Chemotherapy',
                                                rate: '33%',
                                                n: '51/154',
                                                ci: '95% CI, 25.8–41.1',
                                                completeResponseRate: '4%',
                                                partialResponseRate: '29%',
                                            }
                                        ]
                                    }
                                />
                            
                                {/* <ChartTitle as='H3' dangerouslySetInnerHTML={{ __html: "Duration of Response (DOR)<sup>o,p</sup>" }}/> */}
                                <ComparisonTable 
                                    title="Duration of Response (DOR)<sup>o,p</sup>"
                                    limitation="The median follow-up time at the final analysis was 38.1 months (range 0.2 to 58.7 months)"
                                    data={
                                        [
                                            [
                                                {
                                                    legendTitle: 'KEYTRUDA',
                                                },
                                                {
                                                    legendTitle: 'Chemotherapy',
                                                },

                                            ],
                                            [
                                                {
                                                    label: 'Median DOR<sup>o</sup>',
                                                    dataPoint: 'Not reached',
                                                    dataSubPoint: '(range: 2.3+<sup>q</sup> to 41.4+<sup>q</sup> months)'
                                                },
                                                {
                                                    label: 'Median DOR<sup>o</sup>',
                                                    dataPoint: '10.6 months',
                                                    dataSubPoint: '(range: 2.8 to 37.5+<sup>q</sup> months)'
                                                },
                                            ]

                                        ]
                                    } 
                                />

                                <ComparisonTable 
                                    title="DOR: 5-Year Follow-up<sup>1</sup>" 
                                    limitation="<b>LIMITATIONS:</b> The 5-year follow-up of KN-⁠177 was a post hoc, exploratory analysis. No formal statistical testing was planned for this analysis and, therefore, no statistical conclusions can be drawn." 
                                    definitions=""
                                    footnotes={DORtableFootnotes} 
                                    data={
                                        [
                                            [
                                                {
                                                    legendTitle: 'KEYTRUDA',
                                                },
                                                {
                                                    legendTitle: 'Chemotherapy',
                                                },

                                            ],
                                            [
                                                {
                                                    label: 'Median DOR',
                                                    dataPoint: '75.4 months',
                                                    dataSubPoint: '(range: 2.3+<sup>q</sup> to 80.1+<sup>q</sup>)'
                                                },
                                                {
                                                    label: 'Median DOR',
                                                    dataPoint: '10.6 months',
                                                    dataSubPoint: '(range: 2.8 to 71.5+<sup>q</sup> months)'
                                                },
                                            ]

                                        ]
                                    } 
                                />
                            
                        </PageSection>
                        
                        <PageSection bgColor="cloud" title={anchorLinkModelData[1].label}>
                            <MSI_CRC />
                        </PageSection>

                        <PageSection bgColor="cloud" title={anchorLinkModelData[2].label}>
                            <PageSubSection title="Hypothetical Patients Based on Those Studied in KEYNOTE-177">
                                <PatientProfile {...patientProfileEvelyn} />
                                <PatientProfile {...patientProfileBryan} />
                            </PageSubSection>
                        </PageSection>
                    </TemplateColumn>

                    {/* End Main Column */}
                    <TemplateColumn id="template-e-sidebar">
                        <PageSection bgColor="mist">
                            <LinkContent>
                            <CrossLink {...crossLinkContent} />
                            </LinkContent>
                            
                        </PageSection>
                    </TemplateColumn>

                </TemplateColumnWithSidebarContainer>

            <TemplateESsecondary>
                <TemplateColumn id="template-e-secondary">
                    <PageSection bgColor="keytrudaGreen">
                        <RelatedContent items={relatedContent} />
                    </PageSection>
                    <PageSection bgColor="mist">
                        <ReferencesBlock references={pageReferences} />
                    </PageSection>

                </TemplateColumn>
            </TemplateESsecondary>

            </div>
        </DefaultLayout>
    );
};

export default Page;